import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatPaginator as MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource as MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
import { ApihttpMethods, EAuthorizationType } from "src/app/models/general-enums";
import { ApiMapperListItem, DuplicatedDataSource } from "src/app/models/module.model";
import { DataSourceService } from "src/app/services/data-source.service";

const VIEW_STATE_KEY = "datasourceListView";

/** Kept here because several other list screens import this row shape. */
export interface PeriodicElement {
  id: number;
  Name: string;
  url: string;
  type: string;
}

@Component({
  selector: "app-data-source",
  templateUrl: "./data-source.component.html",
  styleUrls: ["./data-source.component.css"],
})
export class DataSourceComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  displayedColumns: string[] = ["method", "name", "url", "auth", "id", "action"];
  dataSource = new MatTableDataSource<ApiMapperListItem>([]);

  searchTerm = "";
  loading = true;
  loadFailed = false;
  /** Id of the row being copied, so only that row shows a spinner. */
  duplicatingId: number = null;

  constructor(
    private dataSourceService: DataSourceService,
    private router: Router,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.dataSource.filterPredicate = (row: ApiMapperListItem, filter: string) =>
      [
        row.name,
        row.description,
        row.url,
        this.methodLabel(row.httpMethod),
        this.authLabel(row.authorizationType),
        "" + row.id,
      ]
        .join(" ")
        .toLowerCase()
        .indexOf(filter) !== -1;

    this.dataSource.sortingDataAccessor = (row: ApiMapperListItem, column: string) => {
      switch (column) {
        case "method":
          return this.methodLabel(row.httpMethod);
        case "name":
          return (row.name || "").toLowerCase();
        case "url":
          return (row.url || "").toLowerCase();
        case "auth":
          return this.authLabel(row.authorizationType);
        default:
          return row.id;
      }
    };

    this.restoreViewState();
    this.load();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    // Coming back from the editor should land on the same filter and page.
    sessionStorage.setItem(
      VIEW_STATE_KEY,
      JSON.stringify({
        searchTerm: this.searchTerm,
        pageIndex: this.paginator ? this.paginator.pageIndex : 0,
        pageSize: this.paginator ? this.paginator.pageSize : 10,
      }),
    );
  }

  private restoreViewState() {
    const stored = sessionStorage.getItem(VIEW_STATE_KEY);
    if (!stored) {
      return;
    }
    try {
      const state = JSON.parse(stored);
      this.searchTerm = state.searchTerm || "";
      this.dataSource.filter = this.searchTerm.trim().toLowerCase();
      if (this.paginator) {
        this.paginator.pageSize = state.pageSize || 10;
        this.paginator.pageIndex = state.pageIndex || 0;
      }
    } catch {
      sessionStorage.removeItem(VIEW_STATE_KEY);
    }
  }

  load() {
    this.loading = true;
    this.loadFailed = false;
    const keepPage = this.paginator ? this.paginator.pageIndex : 0;

    this.dataSourceService
      .getApiMapperList()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (rows: ApiMapperListItem[]) => {
          this.dataSource.data = rows || [];
          if (this.paginator) {
            const lastPage = Math.max(
              0,
              Math.ceil(this.dataSource.filteredData.length / this.paginator.pageSize) - 1,
            );
            this.paginator.pageIndex = Math.min(keepPage, lastPage);
          }
        },
        () => {
          this.loadFailed = true;
          this.dataSource.data = [];
        },
      );
  }

  applyFilter(value: string) {
    this.searchTerm = value;
    this.dataSource.filter = value.trim().toLowerCase();
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  clearFilter() {
    this.applyFilter("");
  }

  get isEmpty(): boolean {
    return !this.loading && !this.loadFailed && this.dataSource.data.length === 0;
  }

  get hasNoMatches(): boolean {
    return (
      !this.loading &&
      !this.loadFailed &&
      this.dataSource.data.length > 0 &&
      this.dataSource.filteredData.length === 0
    );
  }

  get resultCount(): number {
    return this.dataSource.filteredData.length;
  }

  methodLabel(method: ApihttpMethods): string {
    switch (+method) {
      case ApihttpMethods.POST:
        return "POST";
      case ApihttpMethods.GET:
        return "GET";
      case ApihttpMethods.PUT:
        return "PUT";
      case ApihttpMethods.DELETE:
        return "DELETE";
      case ApihttpMethods.PATCH:
        return "PATCH";
      default:
        return "—";
    }
  }

  methodClass(method: ApihttpMethods): string {
    return "ds-method ds-method--" + this.methodLabel(method).toLowerCase();
  }

  authLabel(type: EAuthorizationType): string {
    switch (+type) {
      case EAuthorizationType.Basic:
        return "Basic";
      case EAuthorizationType.JWT:
        return "JWT";
      case EAuthorizationType.Cookies:
        return "Cookies";
      default:
        return "None";
    }
  }

  isSecured(type: EAuthorizationType): boolean {
    return +type !== EAuthorizationType.None;
  }

  /**
   * Renders the URL with its path placeholders picked out, so a parameterised
   * endpoint is recognisable at a glance. The input is escaped first.
   */
  urlHtml(url: string): string {
    const escaped = (url || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    return escaped
      .replace(/\{[^}\/\s]+\}/g, (match) => `<span class="ds-url__token">${match}</span>`)
      .replace(/\/:([A-Za-z0-9_]+)/g, (match, name) => `/<span class="ds-url__token">:${name}</span>`);
  }

  copyId(row: ApiMapperListItem) {
    const value = "" + row.id;
    const done = () => this.toastr.success(`Data source id ${value} copied`, "");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done, () => this.copyFallback(value, done));
    } else {
      this.copyFallback(value, done);
    }
  }

  private copyFallback(value: string, done: () => void) {
    const helper = document.createElement("textarea");
    helper.value = value;
    helper.style.position = "fixed";
    helper.style.opacity = "0";
    document.body.appendChild(helper);
    helper.select();
    document.execCommand("copy");
    document.body.removeChild(helper);
    done();
  }

  editDataSource(row: ApiMapperListItem) {
    this.router.navigate(["/data-source/edit", row.id]);
  }

  /**
   * Copies the whole data source server side - URL, authorization keys, inputs,
   * outputs and response path - under a free "(Copy)" name, then opens the copy
   * so the change you duplicated it for can be made right away.
   */
  duplicateDataSource(row: ApiMapperListItem) {
    if (this.duplicatingId) {
      return;
    }
    this.duplicatingId = row.id;

    this.dataSourceService
      .duplicateDataSource(row.id)
      .pipe(finalize(() => (this.duplicatingId = null)))
      .subscribe(
        (result: DuplicatedDataSource) => {
          this.toastr.success(`Created ${result.name}`, "", {
            timeOut: 3000,
            progressBar: true,
            tapToDismiss: true,
          });
          this.router.navigate(["/data-source/edit", result.id]);
        },
        (error) => {
          this.toastr.error(
            typeof error.error === "string" && error.error
              ? error.error
              : `${row.name} could not be duplicated. Try again.`,
            "",
            { timeOut: 3000, progressBar: true, tapToDismiss: true },
          );
        },
      );
  }

  deleteDataSource(row: ApiMapperListItem) {
    this.dataSourceService.deleteDataSource(row.id).subscribe(
      () => {
        this.toastr.success(`Deleted ${row.name}`, "", {
          timeOut: 2000,
          progressBar: true,
          tapToDismiss: true,
        });
        this.load();
      },
      () => {
        this.toastr.error(`${row.name} could not be deleted. Try again.`, "", {
          timeOut: 3000,
          progressBar: true,
          tapToDismiss: true,
        });
      },
    );
  }
}
